import { decodeMentions, encodeMentions, parseMentionTokens, type MentionRef } from '@supply/contracts'
import { AtSign, Pencil, Send, Trash2 } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { addComment, editComment, removeComment } from '@/data/api'
import type { TaskComment } from '@/data/types'
import { invalidate } from '@/data/use-load'
import { formatDateTime } from '@/lib/format'
import { useMemberName, useSession } from '@/lib/session'
import { useWrite } from '@/lib/use-write'

/**
 * Comentários da tarefa com @menção. Qualquer membro comenta em qualquer
 * tarefa (a RAS é conjunta); o citado recebe aviso no sino. A menção é
 * guardada como marcador `@[Nome](p:<uuid>)` (mesmo formato do Hub SISTEMAS).
 */
export function CommentsPanel({ taskId, comments }: { taskId: string; comments: TaskComment[] }) {
  const { member } = useSession()
  const name = useMemberName()
  const { run, pending } = useWrite()
  const { confirm, confirmDialog } = useConfirm()
  const [editing, setEditing] = React.useState<string | null>(null)

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {comments.map((c) => (
          <li key={c.id} className="rounded-md border border-border p-3">
            <div className="mb-1 flex items-center justify-between gap-2 text-[12px] text-muted-foreground">
              <span>
                <b className="font-medium text-foreground">{name(c.author_id)}</b> · {formatDateTime(c.created_at)}
                {c.edited_at && ' · editado'}
              </span>
              {c.author_id === member.id && editing !== c.id && (
                <span className="flex gap-1">
                  <Button variant="ghost" size="icon-sm" aria-label="Editar comentário" onClick={() => setEditing(c.id)}>
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Apagar comentário"
                    onClick={() =>
                      void confirm({ title: 'Apagar comentário?', destructive: true, confirmLabel: 'Apagar' }).then(
                        (ok) => ok && run(removeComment(c.id)).then((r) => r.ok && invalidate()),
                      )
                    }
                  >
                    <Trash2 />
                  </Button>
                </span>
              )}
            </div>
            {editing === c.id ? (
              <Composer
                initial={c.body}
                submitLabel="Salvar"
                pending={pending}
                onCancel={() => setEditing(null)}
                onSubmit={async (body) => {
                  const r = await run(editComment(c.id, body))
                  if (r.ok) {
                    setEditing(null)
                    invalidate()
                  }
                  return r.ok
                }}
              />
            ) : (
              <MentionText text={c.body} />
            )}
          </li>
        ))}
        {!comments.length && <li className="text-sm text-muted-foreground">Nenhum comentário ainda.</li>}
      </ul>
      <Composer
        submitLabel="Comentar"
        pending={pending}
        onSubmit={async (body) => {
          const r = await run(addComment(taskId, member.id, body))
          if (r.ok) invalidate()
          return r.ok
        }}
      />
      {confirmDialog}
    </div>
  )
}

export function MentionText({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap break-words text-sm">
      {parseMentionTokens(text).map((s, i) =>
        s.type === 'text' ? (
          <React.Fragment key={i}>{s.text}</React.Fragment>
        ) : (
          <span key={i} className="rounded-sm bg-primary/10 px-1 font-medium text-primary">
            @{s.label}
          </span>
        ),
      )}
    </p>
  )
}

function Composer({
  initial = '',
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  initial?: string
  submitLabel: string
  pending: boolean
  onSubmit: (body: string) => Promise<boolean>
  onCancel?: () => void
}) {
  const { ref } = useSession()
  const decoded = React.useMemo(() => decodeMentions(initial), [initial])
  const [text, setText] = React.useState(decoded.text)
  const [refs, setRefs] = React.useState<MentionRef[]>(decoded.refs)
  const [open, setOpen] = React.useState(false)
  const areaRef = React.useRef<HTMLTextAreaElement>(null)

  function mention(id: string, label: string) {
    const display = `@${label}`
    const el = areaRef.current
    const at = el?.selectionStart ?? text.length
    const next = `${text.slice(0, at)}${text.slice(0, at).endsWith(' ') || at === 0 ? '' : ' '}${display} ${text.slice(at)}`
    setText(next)
    setRefs((r) => (r.some((x) => x.id === id) ? r : [...r, { kind: 'pessoa', id, label }]))
    setOpen(false)
    requestAnimationFrame(() => el?.focus())
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault()
        const body = encodeMentions(text.trim(), refs)
        if (!body) return
        void onSubmit(body).then((ok) => {
          if (ok && !onCancel) {
            setText('')
            setRefs([])
          }
        })
      }}
    >
      <Textarea
        ref={areaRef}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Escreva um comentário. Use @ para citar alguém."
        aria-label="Comentário"
        onKeyDown={(e) => {
          if (e.key === '@') {
            e.preventDefault()
            setOpen(true)
          }
        }}
      />
      <div className="flex items-center justify-between gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm">
              <AtSign /> Citar pessoa
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar pessoa" />
              <CommandList>
                <CommandEmpty>Ninguém encontrado.</CommandEmpty>
                {ref.members
                  .filter((m) => m.is_active)
                  .map((m) => (
                    <CommandItem key={m.id} value={m.full_name} onSelect={() => mention(m.id, m.full_name)}>
                      {m.full_name}
                    </CommandItem>
                  ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          <Button type="submit" size="sm" disabled={pending || !text.trim()}>
            <Send /> {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}
